import { useEffect } from "react";

/**
 * Overrides the document background while a page is mounted so the iOS Safari
 * overscroll / safe-area regions match the page instead of revealing the global
 * light app background (`body` has a light gradient — see index.css). Because
 * the app scrolls inside an inner container, the rubber-band bounce and the
 * `viewport-fit=cover` safe areas otherwise paint that light body color at the
 * very top and bottom of the screen.
 *
 * Because `body` never scrolls in this app (it's `overflow: hidden`; an inner
 * container scrolls), a background painted here stays locked to the viewport —
 * so a gradient anchored to the top/bottom edges makes the overscroll gap read
 * as a continuation of the page's first/last sections rather than a flat block.
 *
 * Optionally updates the `theme-color` meta so the browser chrome matches too.
 * The previous values are restored on unmount.
 *
 * @param background  Any CSS `background` value applied to <html> and <body>
 *                    (a solid color, or a gradient matching the page's edges)
 * @param themeColor  Optional color for the <meta name="theme-color"> chrome tint
 */
export default function usePageBackground(background: string, themeColor?: string) {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.background;
    const prevBody = body.style.background;

    // Use the `background` shorthand so it overrides the gradient set in CSS.
    html.style.background = background;
    body.style.background = background;

    const meta = themeColor
      ? document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
      : null;
    const prevTheme = meta?.getAttribute("content") ?? null;
    if (meta && themeColor) meta.setAttribute("content", themeColor);

    return () => {
      html.style.background = prevHtml;
      body.style.background = prevBody;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, [background, themeColor]);
}
